import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { formatDate, titleCase } from '../../lib/format'
import type { ListResponse, TeamMember } from '../../types'
import {
  Alert, Avatar, Card, CardBody, CardHeader, EmptyState, Skeleton, StatusBadge,
} from '../../components/ui'
import { UserIcon } from '../../components/icons'

export default function Team() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['leader', 'team'],
    queryFn: () => api.get<ListResponse<TeamMember>>('/leader/team'),
  })

  const items = data?.items ?? []

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Team</h1>
          <p className="page-head__sub">Members of your cooperative.</p>
        </div>
      </div>

      {/* Inviting is closed: POST /users refuses a cooperative leader now,
          because every account is created and assigned by the platform
          administrator. Saying so beats a button that always fails. */}
      <Alert tone="info">
        Team members are added by the platform administrator. Contact them to have
        someone assigned to your cooperative.
      </Alert>

      <Card>
        <CardHeader title="Members"
                    sub={items.length ? `${items.length} member${items.length === 1 ? '' : 's'}` : undefined} />
        <CardBody flush>
          {isError ? (
            <div style={{ padding: 'var(--sp-6)' }}><Alert tone="danger">{(error as Error).message}</Alert></div>
          ) : isLoading ? (
            <div style={{ padding: 'var(--sp-6)' }} className="stack">
              <Skeleton h={22} /><Skeleton h={22} w="78%" /><Skeleton h={22} w="64%" />
            </div>
          ) : items.length === 0 ? (
            <EmptyState icon={<UserIcon size={22} />} title="No members yet"
                        body="People added to your cooperative will be listed here." />
          ) : (
            <>
              {/* PWA gets tap rows (the 5-column table clipped Contact/Joined
                  off a 390px screen); desktop keeps the full table. */}
              <div className="mobile-only">
                {items.map((m) => (
                  <Link key={m.uid} to={`/team/${m.uid}`} className="lrow">
                    <Avatar className="usr__avatar" photoUrl={m.photo_url} name={m.name} email={m.email} />
                    <div className="lrow__main">
                      <div className="lrow__title">{m.name || m.email || '—'}</div>
                      <div className="lrow__sub">{[titleCase(m.role), m.phone].filter(Boolean).join(' · ')}</div>
                    </div>
                    <div className="lrow__end"><StatusBadge status={m.status} /></div>
                  </Link>
                ))}
              </div>
            <div className="table-wrap desktop-only">
              <table className="table">
                <thead>
                  <tr><th>Member</th><th>Role</th><th>Contact</th><th>Joined</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {items.map((m) => (
                    <tr key={m.uid}>
                      <td>
                        <Link to={`/team/${m.uid}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                          <div className="row" style={{ gap: 'var(--sp-3)' }}>
                            <Avatar className="usr__avatar" photoUrl={m.photo_url} name={m.name} email={m.email} />
                            <div>
                              <div style={{ fontWeight: 550 }}>{m.name || '—'}</div>
                              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                                {m.email || ''}
                              </div>
                            </div>
                          </div>
                        </Link>
                      </td>
                      <td>{titleCase(m.role)}</td>
                      <td>{m.phone || '—'}</td>
                      <td>{formatDate(m.joined_at)}</td>
                      <td><StatusBadge status={m.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
        </CardBody>
      </Card>
    </>
  )
}

